'use client';

// Libraries
import { SessionProvider } from 'next-auth/react';
import Head from 'next/head';
import { useState } from 'react';

// Theme
import { BaseLayout, LogoutWrapper } from '@layout/index';
import { ErrorBoundary } from '@molecules/index';
import { ChakraWrapper } from '@theme/index';
// Types
import { AppPropsWithLayout } from '@globalTypes/index';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
/**
 * Main App component that wraps the entire application.
 * @param {AppPropsWithLayout} props - The props for the App component.
 * @param {React.ComponentType} props.Component - The component to be rendered.
 * @param {Object} props.pageProps - The page props for the component.
 * @param {Object} props.pageProps.session - The session object.
 * @param {Object} props.pageProps.cookies - The cookies object.
 * @returns {ReactElement} The rendered App component.
 */
const App = ({ Component, pageProps: { session, ...pageProps } }: AppPropsWithLayout) => {
  const renderWithLayout = Component.getLayout ?? ((page) => <BaseLayout>{page}</BaseLayout>);
  const [queryClient] = useState(() => new QueryClient());

  return (
    <ChakraWrapper cookies={pageProps.cookies}>
      <ErrorBoundary global>
        <QueryClientProvider client={queryClient}>
          <SessionProvider session={session}>
            <LogoutWrapper>
              <Head>
                <meta content='width=device-width, initial-scale=1' name='viewport' />
              </Head>
              {renderWithLayout(<Component {...pageProps} />)}
              <ReactQueryDevtools initialIsOpen={false} />
            </LogoutWrapper>
          </SessionProvider>
        </QueryClientProvider>
      </ErrorBoundary>
    </ChakraWrapper>
  );
};

export default App;
